/**
 * Supabase Client - データアクセス専用
 * 認証は better-auth が担当。このモジュールはデータアクセス・リアルタイム・ストレージ用。
 */

import { createClient as createSupabaseClient } from '@supabase/supabase-js'

import { checkServiceRoleRuntimeGuard } from './service-role-guard'

// `ReturnType<typeof createSupabaseClient>` では Schema が never に解決される (supabase-js v2.88 の型挙動)。
// 明示的に <any, 'public', 'public'> を指定することで Schema = any になり .from() / .storage が正しく動く。
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- supabase-js v2.88 requires `any` for Schema to enable .from()/.storage
type SupabaseClientType = ReturnType<typeof createSupabaseClient<any, 'public', 'public'>>

// ブラウザ用シングルトン
let browserClient: SupabaseClientType | null = null

/**
 * ブラウザ用 Supabase クライアント（Anon Key、RLS適用）
 */
export function getSupabaseClient(): SupabaseClientType {
  if (!browserClient) {
    const url = (process.env.NEXT_PUBLIC_SUPABASE_URL || '').trim()
    const anonKey = (process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || '').trim()
    browserClient = createSupabaseClient(url, anonKey)
  }
  return browserClient
}

// BT 互換エイリアス: BT は `import { supabase } from '@/lib/supabase/supabase'` を使用
// getServerSupabaseClient() を共有して接続を1本に集約
export const supabase = new Proxy({} as SupabaseClientType, {
  get(_t, prop) {
    const client = getServerSupabaseClient()
    return client[prop as keyof SupabaseClientType]
  },
})

/** exclude_chat=true のモデルハウス ID セットを返す (BT lib/supabase.ts 互換) */
export async function getExcludeChatSet(): Promise<Set<string>> {
  const client = getServerSupabaseClient()
  const { data } = await client.from('model_house_meta').select('model_house_id, exclude_chat')
  const set = new Set<string>()
  if (data) {
    for (const m of data as Array<{ model_house_id: string; exclude_chat: boolean }>) {
      if (m.exclude_chat) set.add(m.model_house_id)
    }
  }
  return set
}

// サーバーサイド用シングルトン
let serverClient: SupabaseClientType | null = null

/**
 * サーバーサイド用 Supabase クライアント（Service Role Key、RLSバイパス）
 * APIルートやサーバーコンポーネントからのデータアクセスに使用
 *
 * @deprecated Phase D-4 (security-roadmap-2026-08) の段階移行で廃止予定。
 *   - cron / batch / admin: `getServiceRoleSupabaseClient` (lib/supabase/supabase-service-role.ts) へ
 *   - user-facing route: `getTenantScopedSupabaseClient` (lib/supabase/supabase-tenant-scoped.ts) へ
 *   段階移行のため本関数は当面維持するが、新規 import は禁止。ESLint baseline で既存違反を pin する。
 *   詳細: docs/システム構成/セキュリティ工程表-2026-08/01-DB-役割移行.md
 */
export function getServerSupabaseClient(): SupabaseClientType {
  if (typeof window !== 'undefined') {
    return getSupabaseClient()
  }

  checkServiceRoleRuntimeGuard('getServerSupabaseClient')

  if (!serverClient) {
    const url = (process.env.NEXT_PUBLIC_SUPABASE_URL || '').trim()
    const serviceKey = (process.env.SUPABASE_SERVICE_ROLE_KEY || '').trim()

    if (!url || !serviceKey) {
      throw new Error(
        '[Supabase] NEXT_PUBLIC_SUPABASE_URL または SUPABASE_SERVICE_ROLE_KEY が未設定です'
      )
    }

    serverClient = createSupabaseClient(url, serviceKey, {
      auth: {
        autoRefreshToken: false,
        persistSession: false,
      },
    })
  }
  return serverClient
}
